import type { NavItem } from "Shared/Layouts/components/NavButton";
import { SimpleTooltip } from "Shared/SimpleTooltip";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

/**
 * Shared color for a nav item's icon (expanded drawer item + topbar pill).
 * Active items get the brand color; everything else (incl. disabled) is muted.
 */
export const navIconColor = (active: boolean, disabled: boolean): string =>
	!disabled && active ? "text-primary" : "text-muted-foreground";

type Props = {
	item: NavItem;
	className: string;
	children: ReactNode;
	/** aria-label for the control. Omit where the label is shown inline. */
	ariaLabel?: string;
	/** Wrap in a hover tooltip (icon-only collapsed drawer + topbar pill). */
	tooltip?: boolean;
	/** Layout wrapper for the disabled button so it stays a valid hover target. */
	disabledTooltipWrapperClass?: string;
};

/**
 * The Button-vs-Link core shared by every nav item: a real `<Link>` (with
 * route preloading on hover/focus) when navigable, or a disabled ghost
 * `<Button>` otherwise — optionally wrapped in a tooltip. Lets `NavButton` and
 * `HorizontalNavButton` keep only their own `className`/icon/content and not
 * re-implement this branching three times.
 */
export const NavItemLink = ({
	item,
	className,
	children,
	ariaLabel,
	tooltip,
	disabledTooltipWrapperClass,
}: Props) => {
	const disabled = !!item.disabled;

	if (disabled || !item.to) {
		const button = (
			<Button
				variant="ghost"
				disabled
				aria-label={ariaLabel}
				className={className}
			>
				{children}
			</Button>
		);
		if (!tooltip) return button;
		const content =
			disabled && item.hint ? `${item.label} · ${item.hint}` : item.label;
		return (
			<SimpleTooltip content={content}>
				<span className={disabledTooltipWrapperClass}>{button}</span>
			</SimpleTooltip>
		);
	}

	const link = (
		<Link
			to={item.to}
			aria-label={ariaLabel}
			className={className}
			onMouseEnter={item.preload}
			onFocus={item.preload}
		>
			{children}
		</Link>
	);
	if (!tooltip) return link;
	return <SimpleTooltip content={item.label}>{link}</SimpleTooltip>;
};
